import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import {
  CIBUS_DIRECTION_LABELS,
  CIBUS_KIND_LABELS,
  CIBUS_STATUS_LABELS,
  resolveStatusClass,
} from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type CibusCallLogsTableProps = Pick<
  UseMonitoringPage,
  | 'cibusRows'
  | 'isCibusLoading'
  | 'hasLoadedCibus'
  | 'cibusTotalPages'
  | 'cibusPage'
  | 'setCibusPage'
  | 'openCibusDetail'
>;

const statusBadgeClass = (status: string) => {
  if (status === 'success') return classes.levelBadgeMuted;
  if (status === 'timeout') return classes.levelBadgeWarn;
  return classes.levelBadgeError;
};

const httpBadgeClass = (statusClass: '4xx' | '5xx' | null) =>
  statusClass === '5xx' ? classes.statusBadge5xx : statusClass === '4xx' ? classes.statusBadge4xx : classes.levelBadgeMuted;

export const CibusCallLogsTable: React.FC<CibusCallLogsTableProps> = ({
  cibusRows,
  isCibusLoading,
  hasLoadedCibus,
  cibusTotalPages,
  cibusPage,
  setCibusPage,
  openCibusDetail,
}) => {
  if (isCibusLoading && !hasLoadedCibus) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table} data-testid="monitoring-cibus-table">
        <thead>
          <tr>
            <th>{'תאריך ושעה'}</th>
            <th>{'עסק'}</th>
            <th>{'כיוון'}</th>
            <th>{'סוג'}</th>
            <th>{'סטטוס'}</th>
            <th>{'קוד HTTP'}</th>
            <th>{'זמן תגובה'}</th>
            <th>{'מזהה בקשה'}</th>
            <th>{'שגיאה'}</th>
          </tr>
        </thead>
        <tbody>
          {cibusRows.map((row) => (
            <tr
              key={row.id}
              className={classes.clickableRow}
              role="button"
              tabIndex={0}
              data-testid={`monitoring-cibus-row-${row.id}`}
              onClick={() => openCibusDetail(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openCibusDetail(row);
                }
              }}
            >
              <td data-label={'תאריך ושעה'}>{formatDateTime(row.createdAt)}</td>
              <td data-label={'עסק'}>{row.companyName ?? row.tenantId ?? '-'}</td>
              <td data-label={'כיוון'}>{CIBUS_DIRECTION_LABELS[row.direction] ?? row.direction}</td>
              <td data-label={'סוג'}>{CIBUS_KIND_LABELS[row.kind] ?? row.kind}</td>
              <td data-label={'סטטוס'}>
                <span className={`${classes.detailKindBadge} ${statusBadgeClass(row.status)}`}>
                  {CIBUS_STATUS_LABELS[row.status] ?? row.status}
                </span>
              </td>
              <td data-label={'קוד HTTP'}>
                {row.httpStatus != null ? (
                  <span className={`${classes.detailKindBadge} ${httpBadgeClass(resolveStatusClass(row.httpStatus))}`}>
                    {row.httpStatus}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td data-label={'זמן תגובה'}>{row.latencyMs != null ? `${row.latencyMs} ms` : '-'}</td>
              <td data-label={'מזהה בקשה'}>{row.requestId ?? '-'}</td>
              <td data-label={'שגיאה'}>
                <div className={classes.errorMessageCell}>{row.errorMessage ?? '-'}</div>
              </td>
            </tr>
          ))}
          {cibusRows.length === 0 && (
            <tr>
              <td colSpan={9} className={classes.empty}>
                {'אין רשומות מעקב סיבוס לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {cibusTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={cibusTotalPages} value={cibusPage} onChange={setCibusPage} size="sm" />
        </div>
      )}
    </div>
  );
};
